// Merges the server's activity / watch-history snapshot with the LOCAL
// state instead of overwriting it.
//
// Why: the dashboard refetches both on every mount (view switches
// included), but the periodic sync only pushes local progress every ~10s
// and SPA navigation fires no pagehide flush. A blind
// fetch().then(setState) therefore replaced fresh local progress with a
// stale server snapshot: a video watched seconds ago vanished from
// Recently Watched the moment the learner switched views (QA 07-29), and
// the monthly-minutes counter could dip the same way. Field-wise max /
// newest-wins mirrors the server's own multi-device merge, so the same
// data converges no matter which side is ahead.

const dayMax = (a = {}, b = {}) => {
  const out = {...a};
  Object.entries(b).forEach(([day, secs]) => {
    out[day] = Math.max(Number(out[day]) || 0, Number(secs) || 0);
  });
  return out;
};

const entryRecency = (e) => {
  const t = e?.lastWatched ? new Date(e.lastWatched).getTime() : 0;
  return Number.isFinite(t) ? t : 0;
};

export const mergeWatchHistory = (local, remote) => {
  const l = local && typeof local === "object" ? local : {};
  const r = remote && typeof remote === "object" ? remote : {};
  const out = {};
  new Set([...Object.keys(l), ...Object.keys(r)]).forEach((id) => {
    const le = l[id];
    const re = r[id];
    if (!le || !re) {
      out[id] = le || re;
      return;
    }
    // The side watched most recently owns the resume point; everything
    // monotonic takes the max of both.
    const newer = entryRecency(le) >= entryRecency(re) ? le : re;
    out[id] = {
      ...re,
      ...le,
      lastPosition: newer.lastPosition,
      lastWatched:
        entryRecency(le) >= entryRecency(re) ? le.lastWatched : re.lastWatched,
      maxPosition: Math.max(
        Number(le.maxPosition) || 0,
        Number(re.maxPosition) || 0,
        Number(le.lastPosition) || 0,
        Number(re.lastPosition) || 0,
      ),
      dailyBreakdown: dayMax(re.dailyBreakdown, le.dailyBreakdown),
    };
  });
  return out;
};

export const mergeActivity = (local, remote) => {
  const l = local && typeof local === "object" ? local : {};
  const r = remote && typeof remote === "object" ? remote : {};
  const out = {};
  new Set([...Object.keys(l), ...Object.keys(r)]).forEach((day) => {
    const ld = l[day] || {};
    const rd = r[day] || {};
    const merged = {...rd, ...ld};
    ["mouseMoves", "clicks", "secondsActive", "totalVideoSeconds"].forEach(
      (k) => {
        merged[k] = Math.max(Number(ld[k]) || 0, Number(rd[k]) || 0);
      },
    );
    out[day] = merged;
  });
  return out;
};
